/**
 * Sport Config Registry
 *
 * Central lookup for sport-specific configuration.
 * Use getSportConfig(sport) anywhere you need sport-specific behavior.
 */

import type { Sport } from '@prisma/client';
import type { SportConfig } from './types';
import { nbaConfig } from './nba';
import { nflConfig } from './nfl';
import { nhlConfig } from './nhl';
import { soccerConfig } from './soccer';
import { soccerEplConfig } from './soccer-epl';
import { soccerWorldCupConfig } from './soccer-world-cup';
import { soccerUclConfig } from './soccer-ucl';
import { ncaaConfig } from './ncaa';
import { ncaafConfig } from './ncaaf';

// ============================================================================
// REGISTRY
// ============================================================================

const SPORT_CONFIGS: Record<Sport, SportConfig> = {
  NBA: nbaConfig,
  NFL: nflConfig,
  NHL: nhlConfig,
  SOCCER: soccerConfig, // @deprecated — kept for backward compat
  SOCCER_EPL: soccerEplConfig,
  SOCCER_WORLD_CUP: soccerWorldCupConfig,
  SOCCER_UCL: soccerUclConfig,
  NCAA_BASKETBALL: ncaaConfig,
  NCAA_FOOTBALL: ncaafConfig,
};

// ============================================================================
// PUBLIC API
// ============================================================================

/**
 * Get the configuration for a specific sport
 */
export function getSportConfig(sport: Sport): SportConfig {
  return SPORT_CONFIGS[sport];
}

/**
 * Get all registered sports
 */
export function getAllSports(): Sport[] {
  return Object.keys(SPORT_CONFIGS) as Sport[];
}

/**
 * Get all sport configs
 */
export function getAllSportConfigs(): SportConfig[] {
  return Object.values(SPORT_CONFIGS);
}

/**
 * Check if a prop category is valid for a given sport
 */
export function isValidPropCategory(sport: Sport, category: string): boolean {
  const config = getSportConfig(sport);
  return config.propCategories.some(c => c.id === category);
}

/**
 * Check if a position is valid for a given sport
 */
export function isValidPosition(sport: Sport, position: string): boolean {
  const config = getSportConfig(sport);
  return config.positions.some(p => p.id === position);
}

/**
 * Get the display label for a prop category
 */
export function getPropCategoryLabel(sport: Sport, category: string): string {
  const config = getSportConfig(sport);
  const found = config.propCategories.find(c => c.id === category);
  return found?.label ?? category;
}

/**
 * Get the Odds API market key for a prop category
 */
export function getOddsApiMarket(sport: Sport, category: string): string | null {
  const config = getSportConfig(sport);
  const found = config.propCategories.find(c => c.id === category);
  return found?.oddsApiMarket ?? null;
}

/**
 * Get all Odds API markets for a sport
 */
export function getOddsApiMarkets(sport: Sport): string[] {
  const config = getSportConfig(sport);
  return config.propCategories.map(c => c.oddsApiMarket);
}

/**
 * Get market-to-category mapping for Odds API integration
 * Returns: { "player_points": "POINTS", "player_rebounds": "REBOUNDS", ... }
 */
export function getOddsApiMarketToCategory(sport: Sport): Record<string, string> {
  const config = getSportConfig(sport);
  const mapping: Record<string, string> = {};
  for (const cat of config.propCategories) {
    mapping[cat.oddsApiMarket] = cat.id;
    // Auto-map alternate market keys to the same category
    // e.g., player_points_alternate → POINTS
    if (cat.oddsApiMarket.startsWith('player_')) {
      mapping[`${cat.oddsApiMarket}_alternate`] = cat.id;
    }
  }
  return mapping;
}
